// Inspired by react-hot-toast library
import * as React from "react";

import type { TToastActionElement, TToastProps } from "./Toast";

const TOAST_LIMIT = 1;
const TOAST_REMOVE_DELAY = 5000;
const TOAST_DURATION = 5000;

type TToasterToast = TToastProps & {
  id: string;
  title?: React.ReactNode;
  description?: React.ReactNode;
  action?: TToastActionElement;
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const actionTypes = {
  ADD_TOAST: "ADD_TOAST",
  UPDATE_TOAST: "UPDATE_TOAST",
  DISMISS_TOAST: "DISMISS_TOAST",
  REMOVE_TOAST: "REMOVE_TOAST",
} as const;

let count = 0;

function genId() {
  count = (count + 1) % Number.MAX_SAFE_INTEGER;
  return count.toString();
}

type TActionType = typeof actionTypes;

type TAction =
  | {
      type: TActionType["ADD_TOAST"];
      toast: TToasterToast;
    }
  | {
      type: TActionType["UPDATE_TOAST"];
      toast: Partial<TToasterToast>;
    }
  | {
      type: TActionType["DISMISS_TOAST"];
      toastId?: TToasterToast["id"];
    }
  | {
      type: TActionType["REMOVE_TOAST"];
      toastId?: TToasterToast["id"];
    };

type TState = {
  toasts: TToasterToast[];
};

const toastTimeouts = new Map<string, ReturnType<typeof setTimeout>>();
const dismissTimeouts = new Map<string, ReturnType<typeof setTimeout>>();

const addToRemoveQueue = (toastId: string) => {
  if (toastTimeouts.has(toastId)) {
    return;
  }

  const timeout = setTimeout(() => {
    toastTimeouts.delete(toastId);
    dispatch({
      type: "REMOVE_TOAST",
      toastId: toastId,
    });
  }, TOAST_REMOVE_DELAY);

  toastTimeouts.set(toastId, timeout);
};

const addToDismissQueue = (toastId: string) => {
  // Clear any existing dismiss timeout for this toast
  if (dismissTimeouts.has(toastId)) {
    clearTimeout(dismissTimeouts.get(toastId));
  }

  const timeout = setTimeout(() => {
    dismissTimeouts.delete(toastId);
    dispatch({
      type: "DISMISS_TOAST",
      toastId: toastId,
    });
  }, TOAST_DURATION);

  dismissTimeouts.set(toastId, timeout);
};

export const reducer = (state: TState, action: TAction): TState => {
  switch (action.type) {
    case "ADD_TOAST":
      return {
        ...state,
        toasts: [action.toast, ...state.toasts].slice(0, TOAST_LIMIT),
      };

    case "UPDATE_TOAST":
      return {
        ...state,
        toasts: state.toasts.map((t) =>
          t.id === action.toast.id ? { ...t, ...action.toast } : t,
        ),
      };

    case "DISMISS_TOAST": {
      const { toastId } = action;

      // Clear any pending dismiss timeout
      if (toastId && dismissTimeouts.has(toastId)) {
        clearTimeout(dismissTimeouts.get(toastId));
        dismissTimeouts.delete(toastId);
      }

      // ! Side effects ! - This could be extracted into a dismissToast() action,
      // but I'll keep it here for simplicity
      if (toastId) {
        addToRemoveQueue(toastId);
      } else {
        state.toasts.forEach((toast) => {
          if (dismissTimeouts.has(toast.id)) {
            clearTimeout(dismissTimeouts.get(toast.id));
            dismissTimeouts.delete(toast.id);
          }
          addToRemoveQueue(toast.id);
        });
      }

      return {
        ...state,
        toasts: state.toasts.map((t) =>
          t.id === toastId || toastId === undefined
            ? {
                ...t,
                open: false,
              }
            : t,
        ),
      };
    }
    case "REMOVE_TOAST":
      if (action.toastId === undefined) {
        return {
          ...state,
          toasts: [],
        };
      }
      return {
        ...state,
        toasts: state.toasts.filter((t) => t.id !== action.toastId),
      };
  }
};

const listeners: ((state: TState) => void)[] = [];

let memoryState: TState = { toasts: [] };

function dispatch(action: TAction) {
  memoryState = reducer(memoryState, action);
  listeners.forEach((listener) => {
    listener(memoryState);
  });
}

type TToast = Omit<TToasterToast, "id">;

function toast({ ...props }: TToast) {
  const id = genId();

  const update = (props: TToasterToast) =>
    dispatch({
      type: "UPDATE_TOAST",
      toast: { ...props, id },
    });
  const dismiss = () => dispatch({ type: "DISMISS_TOAST", toastId: id });

  dispatch({
    type: "ADD_TOAST",
    toast: {
      ...props,
      id,
      open: true,
      onOpenChange: (open) => {
        if (!open) dismiss();
      },
    },
  });

  // Automatically dismiss the toast after TOAST_DURATION
  addToDismissQueue(id);

  return {
    id: id,
    dismiss,
    update,
  };
}

function useToast() {
  const [state, setState] = React.useState<TState>(memoryState);

  React.useEffect(() => {
    listeners.push(setState);
    return () => {
      const index = listeners.indexOf(setState);
      if (index > -1) {
        listeners.splice(index, 1);
      }
    };
  }, [state]);

  return {
    ...state,
    toast,
    dismiss: (toastId?: string) => dispatch({ type: "DISMISS_TOAST", toastId }),
  };
}

export { toast, useToast };
